/**
 * Thin, PII-safe console.error / console.warn → telemetry tap.
 *
 * REPLACES the raw console forwarder removed in cca-a7q (audit finding M3). That
 * forwarder monkey-patched EVERY console method — including `console.log` and
 * whole object arguments (parishioner names, vetting DOBs) — and POSTed them
 * UNREDACTED to an unauthenticated `/api/logs/console` echo. That leak channel,
 * and its server route, are gone.
 *
 * This tap folds ONLY `console.error` / `console.warn` into the shared,
 * PII-redacted @cca/telemetry channel via `reportTelemetry` (which redacts email
 * + long-digit runs and hard-caps per session). It is SOURCE-SIDE MINIMISED: it
 * forwards only string arguments and an Error's `name`/`message`, and DROPS
 * every object / array / other non-string argument at the source — it never
 * stringifies a whole object. Window `error` + `unhandledrejection` are already
 * captured by `initTelemetry`, so this only adds the explicit
 * console.error/console.warn signal. Original console output is left untouched.
 *
 * Lives in @cca/ui because it is ONE redaction control, not one per portal: it
 * was previously duplicated byte-for-byte in cca-parishes-web and
 * cca-vetting-web, where the two copies could drift apart independently.
 */
import { reportTelemetry } from './telemetry';

/**
 * Reduce console arguments to a safe, minimal string: keep string args verbatim
 * and an Error's `name: message`; drop everything else (objects/arrays carry PII
 * — names, DOBs). Returns '' when nothing safe remains so the caller can skip.
 */
function minimiseArgs(args: readonly unknown[]): string {
  return args
    .map((arg) => {
      if (typeof arg === 'string') return arg;
      if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
      return null;
    })
    .filter((part): part is string => part !== null)
    .join(' ')
    .trim();
}

/**
 * Tap console.error / console.warn and mirror a minimised, redacted copy into
 * telemetry. Call once at app start, AFTER `initTelemetry` (so the reporter is
 * configured). It preserves and delegates to the original console methods.
 */
export function setupConsoleTelemetry(): void {
  if (typeof console === 'undefined') return;
  const originalError = console.error.bind(console);
  const originalWarn = console.warn.bind(console);

  console.error = (...args: unknown[]): void => {
    originalError(...args);
    const message = minimiseArgs(args);
    if (message) reportTelemetry('client_error', message);
  };
  console.warn = (...args: unknown[]): void => {
    originalWarn(...args);
    const message = minimiseArgs(args);
    if (message) reportTelemetry('client_error', message);
  };
}
